import type { CoverLayout } from '../types/reportTemplate';

/**
 * Product default cover layout.
 *
 * Back-compat contract: an ABSENT / unknown stored `coverLayout` resolves to
 * Hero-Image — byte-identical to the prior hero-only behaviour, so legacy
 * themes / templates / reports render unchanged with zero migration.
 */
export const HERO_COVER_LAYOUT = 'hero-image' as const;

/**
 * The full set of renderable cover layouts. Kept in sync with the `CoverLayout`
 * union in `reportTemplate.ts` and `ReportTheme.coverLayout`. The report-cover
 * pack added the journal-aesthetic title pages (`boundary-plate` /
 * `data-snapshot` / `masthead`) alongside the original
 * `classic` / `hero-image` / `data-dense` / `minimal` set.
 */
export const REPORT_COVER_LAYOUTS = [
  'classic',
  'hero-image',
  'data-dense',
  'minimal',
  'boundary-plate',
  'data-snapshot',
  'masthead',
  // K5 — additive, opt-in dark instrument-panel cover.
  'dark-instrument',
  // Part L (editorial-layouts-l1) — flag-gated editorial-monograph title page.
  // Always renderable here (the renderer only RESOLVES it behind the flag/preset);
  // an unknown legacy value still falls through to the hero-image default.
  'editorial-masthead',
  // Report Themes v2 (VITE_REPORT_THEMES_V2) — the investment / academic /
  // professional title pages. Always renderable here (resolution is preset-driven;
  // the flag only governs whether the preset/cover is OFFERED in the Studio
  // pickers). Without these, normalizeCoverLayout coerced each v2 preset's cover
  // to hero-image (the SiteRagReportView.resolvedCover regression).
  'prospectus',
  'frontispiece',
  'title-block',
  // Golden convergence (GC-W2) — the investment-memo title page (MemoCover);
  // default cover of the `memo` preset / the stock default template.
  'memo',
] as const satisfies readonly CoverLayout[];

// Compile-time exhaustiveness: adding a CoverLayout without registering it
// here is a type error, so renderers and visual gates cannot silently drift.
const _allCoverLayoutsRegistered: Exclude<CoverLayout, (typeof REPORT_COVER_LAYOUTS)[number]> extends never
  ? true
  : never = true;
void _allCoverLayoutsRegistered;

const VALID_COVER_LAYOUTS: ReadonlySet<CoverLayout> = new Set(REPORT_COVER_LAYOUTS);

/**
 * Coerce a stored cover layout to a renderable value.
 *
 * - A KNOWN layout passes through unchanged (the user's / preset's choice is
 *   honoured — this is what lets the journal-aesthetic covers and the Template
 *   Studio picker actually render).
 * - An ABSENT / unknown / corrupt value falls back to the Hero-Image product
 *   default, preserving the legacy back-compat contract.
 */
export function normalizeCoverLayout(layout?: CoverLayout | null): CoverLayout {
  if (layout != null && VALID_COVER_LAYOUTS.has(layout)) return layout;
  return HERO_COVER_LAYOUT;
}

/**
 * Cover layouts that CARRY the report's executive-summary-equivalent on the
 * cover itself (exec-summary-authoritative-design decision 5).
 *
 *   - `hero-image` renders the Executive Summary in place of the cover
 *     disclaimer (which moves bottom-right).
 *   - `masthead` / `classic` are abstract-bearing journal/title pages whose
 *     Abstract IS the executive summary (rendered IN FULL).
 *
 * When `coverCarriesExecSummary(coverLayout)` is true the standalone body
 * "Executive Summary" section AND its TOC entry are SUPPRESSED so the prose
 * is not duplicated. The decision-band / Go-No-Go content is unaffected.
 */
const EXEC_SUMMARY_CARRYING_COVERS: ReadonlySet<CoverLayout> = new Set<CoverLayout>([
  'hero-image',
  'masthead',
  'classic',
  // Part L — the editorial-masthead renders the abstract (= exec summary) IN
  // FULL as its standfirst, exactly like `masthead`, so the body Executive
  // Summary + its TOC entry must be suppressed to avoid duplication.
  'editorial-masthead',
]);

/** True when the cover layout carries the exec-summary-equivalent (drives the
 *  body-section + TOC de-dup). */
export function coverCarriesExecSummary(layout?: CoverLayout | null): boolean {
  return layout != null && EXEC_SUMMARY_CARRYING_COVERS.has(layout);
}

/**
 * Cover layouts that CARRY the Table of Contents on the cover itself (v1.6.0
 * polish-v2 defect 8). These render a compact numbered index below the cover
 * narrative; editorial-masthead uses the house two-column treatment.
 *
 * When `coverCarriesToc(coverLayout)` is true the standalone
 * `[data-section="toc"]` page is SUPPRESSED (mirrors the
 * `coverHasExecSummaryContent` suppression) so the index is not duplicated.
 */
const TOC_CARRYING_COVERS: ReadonlySet<CoverLayout> = new Set<CoverLayout>([
  'masthead',
  // Part L — the editorial-masthead renders the compact numbered TOC on the
  // cover itself (mirrors `masthead`), so the standalone TOC page is suppressed.
  'editorial-masthead',
  // Report Themes v2 — the SIGNAL title-block cover renders the on-cover numbered
  // index (tocEntries), so suppress the duplicate standalone TOC. Prospectus +
  // Frontispiece do NOT carry an on-cover TOC, so they keep the standalone page.
  'title-block',
]);

/** True when the cover layout renders the TOC on the cover itself (drives the
 *  standalone-TOC-section suppression). */
export function coverCarriesToc(layout?: CoverLayout | null): boolean {
  return layout != null && TOC_CARRYING_COVERS.has(layout);
}

/**
 * Resolve the entries carried by a cover.
 *
 * The product contract is unconditional once a carrying cover has at least
 * three entries: the full Contents index remains on page 1, regardless of
 * length or body-column mode. In particular, a long editorial-masthead index
 * must never be re-routed to a standalone Contents page; its renderer owns the
 * explicit two-column split.
 */
export function resolveOnCoverTocEntries<T>(
  layout: CoverLayout | null | undefined,
  entries: readonly T[],
  tocEnabled: boolean | undefined = true,
): T[] | undefined {
  if (!coverCarriesToc(layout) || tocEnabled === false || entries.length < 3) {
    return undefined;
  }
  return [...entries];
}
